import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material'
import { useEffect, useState } from 'react'
import {
  ChangeChannelRequest,
  ChannelEndpoint,
  QuotationExchangeChannel,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import {
  createChannelRequest,
  emailIsValid,
  errorMessage,
  selectableChannels,
} from './quotationExchange.utils'

const endpointKey = (endpoint?: ChannelEndpoint) =>
  endpoint ? `${endpoint.channel}:${endpoint.address || ''}` : ''

const endpointLabel = (endpoint: ChannelEndpoint) =>
  endpoint.displayName
    ? `${endpoint.displayName} (${endpoint.address || 'no address'})`
    : endpoint.address || 'No address'

const withCurrentEndpoint = (
  options: ChannelEndpoint[],
  current?: ChannelEndpoint,
) =>
  current &&
  !options.some((option) => endpointKey(option) === endpointKey(current))
    ? [current, ...options]
    : options

export function ExchangeChannelDialog({
  open,
  title,
  channel: initialChannel,
  sender: initialSender,
  recipient: initialRecipient,
  recipientOptions = [],
  onClose,
  onSave,
}: {
  open: boolean
  title: string
  channel?: QuotationExchangeChannel
  sender?: ChannelEndpoint
  recipient?: ChannelEndpoint
  recipientOptions?: ChannelEndpoint[]
  onClose: () => void
  onSave: (request: ChangeChannelRequest) => Promise<void>
}) {
  const [channel, setChannel] = useState<QuotationExchangeChannel>(
    QuotationExchangeChannel.Scsn,
  )
  const [sender, setSender] = useState<ChannelEndpoint>()
  const [recipient, setRecipient] = useState<ChannelEndpoint>()
  const [error, setError] = useState<string>()
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setChannel(initialChannel || QuotationExchangeChannel.Scsn)
    setSender(initialSender)
    setRecipient(initialRecipient)
    setError(undefined)
  }, [initialChannel, initialRecipient, initialSender, open])

  const save = async () => {
    try {
      setSaving(true)
      setError(undefined)
      await onSave(createChannelRequest(channel, sender, recipient))
      onClose()
    } catch (requestError) {
      setError(errorMessage(requestError))
    } finally {
      setSaving(false)
    }
  }

  const manual = channel === QuotationExchangeChannel.Manual
  const email = channel === QuotationExchangeChannel.Email
  const availableRecipients = withCurrentEndpoint(
    recipientOptions.filter(
      (endpoint) =>
        endpoint.channel === QuotationExchangeChannel.Email &&
        emailIsValid(endpoint.address),
    ),
    recipient?.channel === QuotationExchangeChannel.Email
      ? recipient
      : undefined,
  )
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <FormControl fullWidth>
            <InputLabel>Channel</InputLabel>
            <Select
              label="Channel"
              value={channel}
              onChange={(event) => {
                const nextChannel = event.target
                  .value as QuotationExchangeChannel
                setChannel(nextChannel)
                if (nextChannel !== QuotationExchangeChannel.Email) return
                setSender(
                  initialSender?.channel === nextChannel
                    ? initialSender
                    : undefined,
                )
                setRecipient(
                  initialRecipient?.channel === nextChannel
                    ? initialRecipient
                    : undefined,
                )
              }}
            >
              {selectableChannels.map((value) => (
                <MenuItem key={value} value={value}>
                  {value === QuotationExchangeChannel.Scsn ? 'SCSN' : value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {email && (
            <>
              <Alert severity="info">
                Email is sent by QuotationFactory from rfq@quotationfactory.com.
              </Alert>
              <FormControl
                fullWidth
                required
                error={!emailIsValid(recipient?.address)}
              >
                <InputLabel id="exchange-email-recipient-label">
                  Recipient
                </InputLabel>
                <Select
                  labelId="exchange-email-recipient-label"
                  label="Recipient"
                  value={endpointKey(recipient)}
                  onChange={(event) =>
                    setRecipient(
                      availableRecipients.find(
                        (endpoint) =>
                          endpointKey(endpoint) === event.target.value,
                      ),
                    )
                  }
                >
                  {availableRecipients.map((endpoint) => (
                    <MenuItem
                      key={endpointKey(endpoint)}
                      value={endpointKey(endpoint)}
                    >
                      {endpointLabel(endpoint)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
          <Alert severity="info">
            {manual
              ? 'Manual exchange generates a document for download.'
              : email
                ? 'The supplier receives an email with a secure response link. You can also copy that link and share it another way.'
                : 'SCSN sender and recipient endpoints are resolved automatically.'}
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={saving || (email && !emailIsValid(recipient?.address))}
          loading={saving}
          onClick={save}
        >
          Save channel
        </Button>
      </DialogActions>
    </Dialog>
  )
}
